import React, { useState, useEffect } from 'react';
import {
  CustomSVGSeries,
  FlexibleXYPlot,
  Hint,
  VerticalBarSeries,
  VerticalGridLines,
  HorizontalGridLines,
  XAxis,
  YAxis
} from 'react-vis';
import { useHistory, useLocation } from 'react-router-dom';
import TimeSeriesHint from './TimeSeriesHint';
import { getSourceColor, getSourceLabel, commafy } from 'Utils';

// Masking-summary hint: a flat read of the hovered job's bar (field count, completion time, rows
// masked). No diff fetch — searchids here are masking job ids, not discovery run ids.
const MaskingHint = ({ tooltipData }) => {
  const data = tooltipData || {};
  let timeLabel = '—';
  if (data.date) {
    const d = new Date(data.date);
    if (!isNaN(d.getTime())) {
      timeLabel = d.toLocaleString('en-US', {
        month: 'numeric', day: 'numeric', year: '2-digit',
        hour: 'numeric', minute: 'numeric', hour12: true,
      });
    }
  }
  return (
    <div className='timeseries-tooltip'>
      <p>{commafy(data.y || 0)} masked fields</p>
      <p>{timeLabel}</p>
      <p>{data.rowsMasked != null ? commafy(data.rowsMasked) : '—'} rows masked</p>
    </div>
  );
};

const downCaretPath = 'M6.1018 8C5.02785 8 4.45387 9.2649 5.16108 10.0731L10.6829 16.3838C11.3801 17.1806 12.6197 17.1806 13.3169 16.3838L18.8388 10.0731C19.5459 9.2649 18.972 8 17.898 8H6.1018Z';
const upCaretPath = 'M7.00003 15.5C6.59557 15.5 6.23093 15.2564 6.07615 14.8827C5.92137 14.509 6.00692 14.0789 6.29292 13.7929L11.2929 8.79289C11.6834 8.40237 12.3166 8.40237 12.7071 8.79289L17.7071 13.7929C17.9931 14.0789 18.0787 14.509 17.9239 14.8827C17.7691 15.2564 17.4045 15.5 17 15.5H7.00003Z';

const getDisplayDate = (date) => {
  const dateValue = new Date(date);
  const displayDate = dateValue.toLocaleDateString('en-us', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'   
  });
  return displayDate;
}

const CustomSVGComponent = (props) => {
  const difference = props.difference || 0;

  let prefix = '+';
  let caretPath = upCaretPath;
  let color = '#DC2626';

  if (difference < 1) {
    prefix = '';
    caretPath = downCaretPath;
    color= '#16A34A';
  }

  return (
    <g 
      transform='translate(0, -45)'
      textAnchor='middle' 
      className='inner-inner-component'
      style={{ textAlign: 'center' }}
    >
      <svg 
        x='-16' 
        y='0' 
        width='32' 
        height='32' 
        viewBox="0 0 24 24"
      >
        <path 
          d={caretPath} 
          fill={color}
        />
      </svg>
      <text style={{ fontSize: '13px'  }} fill='#315DFF'>
        <tspan x='0' y='2.75em' fill='#315DFF' textDecoration='underline'>
          {prefix}{props.difference} FIELDS
        </tspan>
      </text>
    </g>
  )
}

const TimeSeriesChart = (props) => {
  const [data, setData] = useState([]);
  const [seriesBySource, setSeriesBySource] = useState({});
  const [legend, setLegend] = useState([]);
  const [xTicks, setXTicks] = useState([]);
  const [tooltipData, setTooltipData] = useState(null);
  const [yDomain, setYDomain] = useState([0, 300]);
  const [xDomain, setXDomain] = useState([0, 1]);
  const history = useHistory();

  // Where a bar click navigates. Discovery (default) keeps the discovery results route; the masking
  // summary passes getResultHref to point bars at the per-job masking results page. Navigate from the
  // clicked bar's own id rather than hover state, so a click without a prior hover still works.
  const getResultHref = props.getResultHref || ((id) => `/results/${id}`);
  const goToResult = (value) => {
    const id = value && value.searchids && value.searchids[0];
    if (id != null) history.push(getResultHref(id));
  };

  useEffect(() => {
    const timeSeriesData = props.timeSeriesData || [];
    const dataValue = [];
    // Per-source sparse series keyed by registeredServiceId. Each series holds one entry per x
    // tick; slots where the source had no job are filled with y=0 so stacked bars remain aligned.
    const bySource = {};
    let yDomainValue = [0, 300];
    let xDomainValue = [0, 1];
    let highestSensitiveFieldCount = 0;
    let xIndex = 1;

    timeSeriesData.forEach((dataPoint, i) => {
      const currCount = dataPoint.sensitiveFieldCount;
      const prevCount = timeSeriesData[i - 1]?.sensitiveFieldCount;
      const prevSearchids = timeSeriesData[i - 1]?.searchids;
      const searchids = dataPoint.searchids;
      const difference = (currCount - prevCount) || 0;
      highestSensitiveFieldCount = Math.max(currCount, highestSensitiveFieldCount);
      const itemLabel = getDisplayDate(dataPoint.date);
      const svcId = dataPoint.registeredServiceId || null;

      const item = {
        x: xIndex,
        y: currCount,
        xTickLabel: itemLabel,
        registeredServiceId: svcId,
        date: dataPoint.date,
        rowsMasked: dataPoint.rowsMasked,
        ...prevSearchids && ({ prevSearchids: prevSearchids }),
        searchids: searchids,
        ...difference && (
          {
            customComponent: () => (
              <CustomSVGComponent
                difference={difference}
              />
            )
          }
        ),
      }
      dataValue.push(item);

      const key = svcId || '_c2_native';
      if (!bySource[key]) bySource[key] = [];
      bySource[key].push({
        x: xIndex,
        y: currCount,
        xTickLabel: itemLabel,
        searchids,
        registeredServiceId: svcId,
        date: dataPoint.date,
        rowsMasked: dataPoint.rowsMasked,
      });
      xIndex++;
    });

    // Pad every source series to the same x length with y=0 so react-vis aligns bars.
    Object.keys(bySource).forEach((key) => {
      const filled = new Array(dataValue.length).fill(null).map((_, idx) => {
        const existing = bySource[key].find((p) => p.x === idx + 1);
        if (existing) return existing;
        return { x: idx + 1, y: 0, xTickLabel: dataValue[idx]?.xTickLabel, searchids: [] };
      });
      bySource[key] = filled;
    });

    const legendEntries = Object.keys(bySource).map((key) => {
      const svcId = key === '_c2_native' ? null : key;
      return {
        key,
        color: getSourceColor(svcId),
        label: getSourceLabel(svcId),
      };
    });

    xDomainValue[1] = dataValue.length + 1;
    yDomainValue[1] = highestSensitiveFieldCount + 50;
    const xTicksValue = [null, ...dataValue.map(obj => obj.xTickLabel)];
    setXTicks(xTicksValue);
    setXDomain(xDomainValue);
    setYDomain(yDomainValue);
    setData(dataValue);
    setSeriesBySource(bySource);
    setLegend(legendEntries);

  }, [props.timeSeriesData]);

  return (
    <div 
      style={{position: 'relative'}}
      className='timeseries-chart'
    >
      <FlexibleXYPlot
        margin={{
          bottom: 100
        }}
        height={380}
        yDomain={yDomain}
        xDomain={xDomain}
      >
        <VerticalGridLines />
        <HorizontalGridLines />
        <XAxis 
          style={{ zIndex: '5' }}
          tickValues={xTicks.map((obj, i) => i)}
          tickFormat={(v) => xTicks[v]}
          tickLabelAngle={-45}
        />
        <YAxis />
        <CustomSVGSeries data={data} />
        {Object.keys(seriesBySource).length <= 1 ? (
          <VerticalBarSeries
            barWidth={0.3}
            color={legend[0] ? legend[0].color : '#315DFF'}
            data={data}
            onValueMouseOver={(value) => setTooltipData(value)}
            onValueMouseOut={() => setTooltipData(null)}
            onValueClick={goToResult}
          />
        ) : (
          Object.keys(seriesBySource).map((key) => {
            const entry = legend.find((l) => l.key === key);
            return (
              <VerticalBarSeries
                key={key}
                barWidth={0.3}
                color={entry ? entry.color : '#315DFF'}
                data={seriesBySource[key]}
                onValueMouseOver={(value) => setTooltipData(value)}
                onValueMouseOut={() => setTooltipData(null)}
                onValueClick={goToResult}
              />
            );
          })
        )}
        { tooltipData && (
          <Hint value={tooltipData}>
            { props.maskingHint
              ? <MaskingHint tooltipData={tooltipData} />
              : <TimeSeriesHint tooltipData={tooltipData} tableLabel={props.tableLabel} /> }
          </Hint>
        )}
      </FlexibleXYPlot>
      {legend.length > 1 && (
        <div className='d-flex justify-content-center flex-wrap mt-2'>
          {legend.map((entry) => (
            <div key={entry.key} className='d-flex align-items-center mr-3'>
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: 2,
                  marginRight: 6,
                }}
              />
              <span style={{ fontSize: '0.85rem' }}>{entry.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
};

export default TimeSeriesChart;